<script setup>
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { BRAND_PRIMARY } from 'dashboard/constants/brandConstants';

const props = defineProps({
 name: { type: String, default: '' },
 description: { type: String, default: '' },
 formType: { type: String, default: 'standard' },
 submitAction: { type: String, default: 'message' },
 redirectUrl: { type: String, default: '' },
 thankYouMessage: { type: String, default: '' },
 inboxId: { type: [Number, String], default: null },
 conversionIdentifier: { type: String, default: '' },
 primaryColor: { type: String, default: BRAND_PRIMARY },
 formTheme: { type: String, default: 'light' },
 showBranding: { type: Boolean, default: true },
 alsoCreateConversation: { type: Boolean, default: false },
 redirectRules: { type: Array, default: () => [] },
 exitRules: { type: Array, default: () => [] },
 formFields: { type: Array, default: () => [] },
});

const emit = defineEmits([
 'update:name',
 'update:description',
 'update:formType',
 'update:submitAction',
 'update:redirectUrl',
 'update:thankYouMessage',
 'update:inboxId',
 'update:conversionIdentifier',
 'update:primaryColor',
 'update:formTheme',
 'update:showBranding',
 'update:alsoCreateConversation',
 'update:redirectRules',
 'update:exitRules',
]);

const inboxes = useMapGetter('inboxes/getInboxes');

const formTypes = [
 { value: 'standard', label: 'Padrao', description: 'Formulario de pagina unica' },
 { value: 'multi_step', label: 'Multi-etapa', description: 'Formulario com varias etapas/passos' },
];

const submitActions = [
 { value: 'message', label: 'Criar conversa', description: 'Cria uma conversa no inbox selecionado' },
 { value: 'redirect', label: 'Redirecionar', description: 'Redireciona para uma URL apos envio' },
 { value: 'thank_you', label: 'Mensagem de agradecimento', description: 'Exibe uma mensagem de agradecimento' },
];

const conditionOperators = [
 { value: 'equals', label: 'Igual a' },
 { value: 'not_equals', label: 'Diferente de' },
 { value: 'contains', label: 'Contem' },
 { value: 'not_contains', label: 'Nao contem' },
 { value: 'is_empty', label: 'Esta vazio' },
 { value: 'not_empty', label: 'Nao esta vazio' },
];

const exitActionTypes = [
 { value: 'redirect', label: 'Redirecionar', icon: 'i-lucide-external-link' },
 { value: 'thank_you', label: 'Mensagem de agradecimento', icon: 'i-lucide-message-square' },
 { value: 'conversation', label: 'Criar conversa em inbox', icon: 'i-lucide-inbox' },
];

const needsValueInput = operator => !['is_empty', 'not_empty'].includes(operator);

// Filter to only show input fields (not headings, dividers, etc.)
const selectableFields = computed(() =>
 props.formFields.filter(f =>
 !['heading', 'paragraph', 'divider', 'hidden', 'file'].includes(f.field_type)
 )
);

// Shows inbox selector when: action is 'message' OR alsoCreateConversation is true
const showInboxSelector = computed(
 () => props.submitAction === 'message' || props.alsoCreateConversation
);

function addRedirectRule() {
 const rules = [...props.redirectRules];
 rules.push({
 condition: { field: '', operator: 'equals', value: '' },
 redirect_url: '',
 });
 emit('update:redirectRules', rules);
}

function updateRedirectRule(index, key, value) {
 const rules = [...props.redirectRules];
 if (key.startsWith('condition.')) {
 const condKey = key.split('.')[1];
 rules[index] = {
 ...rules[index],
 condition: { ...rules[index].condition, [condKey]: value },
 };
 } else {
 rules[index] = { ...rules[index], [key]: value };
 }
 emit('update:redirectRules', rules);
}

function removeRedirectRule(index) {
 const rules = [...props.redirectRules];
 rules.splice(index, 1);
 emit('update:redirectRules', rules);
}

// --- Exit Rules ---
function addExitRule() {
 const rules = [...props.exitRules];
 rules.push({
 condition: { field: '', operator: 'equals', value: '' },
 action_type: 'redirect',
 action_config: {},
 });
 emit('update:exitRules', rules);
}

function updateExitRule(index, key, value) {
 const rules = [...props.exitRules];
 if (key.startsWith('condition.')) {
 const condKey = key.split('.')[1];
 rules[index] = {
 ...rules[index],
 condition: { ...rules[index].condition, [condKey]: value },
 };
 } else if (key.startsWith('action_config.')) {
 const configKey = key.split('.')[1];
 rules[index] = {
 ...rules[index],
 action_config: { ...rules[index].action_config, [configKey]: value },
 };
 } else if (key === 'action_type') {
 // Reset action_config when changing action type
 rules[index] = { ...rules[index], action_type: value, action_config: {} };
 } else {
 rules[index] = { ...rules[index], [key]: value };
 }
 emit('update:exitRules', rules);
}

function removeExitRule(index) {
 const rules = [...props.exitRules];
 rules.splice(index, 1);
 emit('update:exitRules', rules);
}
</script>

<template>
 <div class="p-4 space-y-5">
 <h3
 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider"
 >
 Configurações do Formulário
 </h3>

 <!-- Name -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Nome
 </label>
 <input
 :value="name"
 type="text"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="$emit('update:name', $event.target.value)"
 />
 </div>

 <!-- Description -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Descricao
 </label>
 <textarea
 :value="description"
 class="reset-base w-full h-20 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 resize-none focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="$emit('update:description', $event.target.value)"
 />
 </div>

 <!-- Form type -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-2"
 >
 Tipo de formulario
 </label>
 <div class="space-y-1.5">
 <button
 v-for="ft in formTypes"
 :key="ft.value"
 class="flex items-start gap-3 w-full p-3 rounded-lg border transition-colors text-left"
 :class="
 formType === ft.value
 ? 'bg-woot-50 dark:bg-woot-500/10 border-woot-300 dark:border-woot-500/30'
 : 'bg-n-solid-1 border-n-weak hover:border-n-strong dark:hover:border-n-slate-10'
 "
 @click="$emit('update:formType', ft.value)"
 >
 <div
 class="size-4 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center"
 :class="
 formType === ft.value
 ? 'border-woot-500'
 : 'border-n-strong'
 "
 >
 <div
 v-if="formType === ft.value"
 class="size-2 rounded-full bg-woot-500"
 />
 </div>
 <div>
 <span
 class="text-xs font-medium"
 :class="
 formType === ft.value
 ? 'text-woot-700 dark:text-woot-400'
 : 'text-n-slate-11'
 "
 >
 {{ ft.label }}
 </span>
 <p
 class="text-[10px] text-n-slate-10 !mb-0 mt-0.5"
 >
 {{ ft.description }}
 </p>
 </div>
 </button>
 </div>
 </div>

 <hr class="border-n-weak" />

 <!-- Submit action -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-2"
 >
 Ação após envio
 </label>
 <div class="space-y-1.5">
 <button
 v-for="sa in submitActions"
 :key="sa.value"
 class="flex items-start gap-3 w-full p-3 rounded-lg border transition-colors text-left"
 :class="
 submitAction === sa.value
 ? 'bg-woot-50 dark:bg-woot-500/10 border-woot-300 dark:border-woot-500/30'
 : 'bg-n-solid-1 border-n-weak hover:border-n-strong dark:hover:border-n-slate-10'
 "
 @click="$emit('update:submitAction', sa.value)"
 >
 <div
 class="size-4 rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center"
 :class="
 submitAction === sa.value
 ? 'border-woot-500'
 : 'border-n-strong'
 "
 >
 <div
 v-if="submitAction === sa.value"
 class="size-2 rounded-full bg-woot-500"
 />
 </div>
 <div>
 <span
 class="text-xs font-medium"
 :class="
 submitAction === sa.value
 ? 'text-woot-700 dark:text-woot-400'
 : 'text-n-slate-11'
 "
 >
 {{ sa.label }}
 </span>
 <p
 class="text-[10px] text-n-slate-10 !mb-0 mt-0.5"
 >
 {{ sa.description }}
 </p>
 </div>
 </button>
 </div>
 </div>

 <!-- Inbox (shown when action=message OR alsoCreateConversation) -->
 <div v-if="showInboxSelector">
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Inbox
 </label>
 <select
 :value="inboxId"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @change="
 $emit(
 'update:inboxId',
 $event.target.value
 ? Number($event.target.value)
 : null
 )
 "
 >
 <option value="">Selecione um inbox</option>
 <option
 v-for="inbox in inboxes"
 :key="inbox.id"
 :value="inbox.id"
 >
 {{ inbox.name }}
 </option>
 </select>
 </div>

 <!-- Also create conversation (when action is redirect or thank_you) -->
 <div
 v-if="submitAction !== 'message'"
 class="flex items-center justify-between"
 >
 <div>
 <label
 class="text-xs font-medium text-n-slate-11"
 >
 Tambem criar conversa
 </label>
 <p
 class="text-[10px] text-n-slate-9 !mb-0"
 >
 Alem de redirecionar/agradecer, cria uma conversa no inbox
 </p>
 </div>
 <button
 class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
 :class="
 alsoCreateConversation
 ? 'bg-woot-500'
 : 'bg-n-slate-5'
 "
 @click="$emit('update:alsoCreateConversation', !alsoCreateConversation)"
 >
 <span
 class="inline-block size-3.5 transform rounded-full bg-white transition-transform shadow-sm"
 :class="
 alsoCreateConversation ? 'translate-x-4.5' : 'translate-x-1'
 "
 />
 </button>
 </div>

 <!-- Redirect URL -->
 <div v-if="submitAction === 'redirect'">
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 URL de redirecionamento (padrao)
 </label>
 <input
 :value="redirectUrl"
 type="url"
 placeholder="https://exemplo.com/obrigado"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="
 $emit('update:redirectUrl', $event.target.value)
 "
 />
 <p
 class="text-[10px] text-n-slate-9 !mb-0 mt-1"
 >
 URL usada quando nenhuma regra condicional corresponde.
 </p>
 </div>

 <!-- Conditional redirect rules -->
 <div v-if="submitAction === 'redirect'">
 <div class="flex items-center justify-between mb-2">
 <label
 class="text-xs font-medium text-n-slate-11"
 >
 Redirecionamento condicional
 </label>
 <button
 class="text-[10px] font-medium text-woot-500 hover:text-woot-600 border-0 bg-transparent cursor-pointer"
 @click="addRedirectRule"
 >
 + Adicionar regra
 </button>
 </div>

 <div
 v-if="redirectRules.length === 0"
 class="text-[10px] text-n-slate-9 p-3 text-center border border-dashed border-n-weak rounded-lg"
 >
 Sem regras condicionais. Todos os envios usarao a URL padrao.
 </div>

 <div
 v-for="(rule, index) in redirectRules"
 :key="index"
 class="p-3 mb-2 border border-n-weak rounded-lg bg-n-slate-2 space-y-2"
 >
 <div class="flex items-center justify-between">
 <span class="text-[10px] font-semibold text-n-slate-10 uppercase">
 Regra {{ index + 1 }}
 </span>
 <button
 class="text-[10px] text-red-500 hover:text-red-600 border-0 bg-transparent cursor-pointer"
 @click="removeRedirectRule(index)"
 >
 Remover
 </button>
 </div>

 <!-- Condition: field -->
 <div class="grid grid-cols-3 gap-1.5">
 <select
 :value="rule.condition?.field || ''"
 class="reset-base h-8 px-2 text-[11px] rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @change="updateRedirectRule(index, 'condition.field', $event.target.value)"
 >
 <option value="">Campo</option>
 <option
 v-for="field in selectableFields"
 :key="field.name"
 :value="field.name"
 >
 {{ field.label }}
 </option>
 </select>

 <select
 :value="rule.condition?.operator || 'equals'"
 class="reset-base h-8 px-2 text-[11px] rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @change="updateRedirectRule(index, 'condition.operator', $event.target.value)"
 >
 <option
 v-for="op in conditionOperators"
 :key="op.value"
 :value="op.value"
 >
 {{ op.label }}
 </option>
 </select>

 <input
 :value="rule.condition?.value || ''"
 type="text"
 placeholder="Valor"
 class="reset-base h-8 px-2 text-[11px] rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @input="updateRedirectRule(index, 'condition.value', $event.target.value)"
 />
 </div>

 <!-- Redirect URL for this rule -->
 <input
 :value="rule.redirect_url || ''"
 type="url"
 placeholder="https://exemplo.com/pagina-especifica"
 class="reset-base w-full h-8 px-2 text-[11px] rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @input="updateRedirectRule(index, 'redirect_url', $event.target.value)"
 />
 </div>
 </div>

 <!-- Thank you message -->
 <div v-if="submitAction === 'thank_you'">
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Mensagem de agradecimento
 </label>
 <textarea
 :value="thankYouMessage"
 class="reset-base w-full h-20 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 resize-none focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="
 $emit(
 'update:thankYouMessage',
 $event.target.value
 )
 "
 />
 </div>

 <hr class="border-n-weak" />

 <!-- Exit Rules (conditional post-submission actions) -->
 <div>
 <div class="flex items-center justify-between mb-2">
 <div>
 <h4
 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider"
 >
 Regras condicionais de saida
 </h4>
 <p
 class="text-[10px] text-n-slate-9 !mb-0 mt-0.5"
 >
 Sobrescreva a ação padrão baseado nas respostas.
 </p>
 </div>
 <button
 class="flex items-center gap-1 text-xs font-medium text-woot-600 dark:text-woot-400 hover:text-woot-700 dark:hover:text-woot-300 transition-colors border-0 bg-transparent cursor-pointer"
 @click="addExitRule"
 >
 <span class="i-lucide-plus size-3" />
 Regra
 </button>
 </div>

 <!-- Empty state -->
 <div
 v-if="exitRules.length === 0"
 class="flex flex-col items-center py-6 text-center border border-dashed border-n-weak rounded-lg"
 >
 <div
 class="flex items-center justify-center size-9 rounded-xl bg-n-slate-3 mb-2"
 >
 <span class="i-lucide-git-branch size-4 text-n-slate-9" />
 </div>
 <p
 class="text-[10px] text-n-slate-9 !mb-0 max-w-[200px]"
 >
 Sem regras condicionais. Todos os envios usarão a ação padrão
 configurada acima.
 </p>
 </div>

 <!-- Exit rules list -->
 <div v-else class="space-y-2">
 <div
 v-for="(rule, index) in exitRules"
 :key="index"
 class="bg-n-slate-2/30 border border-n-weak rounded-lg p-3"
 >
 <!-- Rule header -->
 <div class="flex items-center justify-between mb-2">
 <span
 class="text-[10px] font-semibold text-n-slate-9 uppercase tracking-wider"
 >
 {{ index === 0 ? 'Se' : 'Senao se' }}
 </span>
 <button
 class="flex items-center justify-center size-5 rounded text-n-slate-9 hover:text-red-500 transition-colors border-0 bg-transparent cursor-pointer"
 @click="removeExitRule(index)"
 >
 <span class="i-lucide-x size-3" />
 </button>
 </div>

 <!-- Condition: field -->
 <div class="mb-2">
 <label
 class="block text-[10px] font-medium text-n-slate-10 mb-0.5"
 >
 Campo
 </label>
 <select
 :value="rule.condition?.field || ''"
 class="reset-base w-full h-8 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @change="
 updateExitRule(
 index,
 'condition.field',
 $event.target.value
 )
 "
 >
 <option value="">Selecione um campo</option>
 <option
 v-for="field in selectableFields"
 :key="field.name"
 :value="field.name"
 >
 {{ field.label }}
 </option>
 </select>
 </div>

 <!-- Condition: operator -->
 <div class="mb-2">
 <label
 class="block text-[10px] font-medium text-n-slate-10 mb-0.5"
 >
 Operador
 </label>
 <select
 :value="rule.condition?.operator || 'equals'"
 class="reset-base w-full h-8 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @change="
 updateExitRule(
 index,
 'condition.operator',
 $event.target.value
 )
 "
 >
 <option
 v-for="op in conditionOperators"
 :key="op.value"
 :value="op.value"
 >
 {{ op.label }}
 </option>
 </select>
 </div>

 <!-- Condition: value (hidden for is_empty/not_empty) -->
 <div
 v-if="needsValueInput(rule.condition?.operator)"
 class="mb-2"
 >
 <label
 class="block text-[10px] font-medium text-n-slate-10 mb-0.5"
 >
 Valor
 </label>
 <input
 :value="rule.condition?.value || ''"
 type="text"
 placeholder="Insira o valor..."
 class="reset-base w-full h-8 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @input="
 updateExitRule(
 index,
 'condition.value',
 $event.target.value
 )
 "
 />
 </div>

 <!-- Action type -->
 <div class="mb-2">
 <label
 class="block text-[10px] font-medium text-n-slate-10 mb-0.5"
 >
 Ação
 </label>
 <select
 :value="rule.action_type || 'redirect'"
 class="reset-base w-full h-8 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @change="
 updateExitRule(
 index,
 'action_type',
 $event.target.value
 )
 "
 >
 <option
 v-for="act in exitActionTypes"
 :key="act.value"
 :value="act.value"
 >
 {{ act.label }}
 </option>
 </select>
 </div>

 <!-- Action config: redirect URL -->
 <div v-if="rule.action_type === 'redirect'">
 <label
 class="block text-[10px] font-medium text-n-slate-10 mb-0.5"
 >
 URL de redirecionamento
 </label>
 <input
 :value="rule.action_config?.redirect_url || ''"
 type="url"
 placeholder="https://exemplo.com/pagina"
 class="reset-base w-full h-8 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @input="
 updateExitRule(
 index,
 'action_config.redirect_url',
 $event.target.value
 )
 "
 />
 </div>

 <!-- Action config: thank you message -->
 <div v-if="rule.action_type === 'thank_you'">
 <label
 class="block text-[10px] font-medium text-n-slate-10 mb-0.5"
 >
 Mensagem de agradecimento
 </label>
 <textarea
 :value="rule.action_config?.thank_you_message || ''"
 placeholder="Obrigado pela sua resposta!"
 class="reset-base w-full h-16 px-2 py-1.5 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 resize-none focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @input="
 updateExitRule(
 index,
 'action_config.thank_you_message',
 $event.target.value
 )
 "
 />
 </div>

 <!-- Action config: inbox selection -->
 <div v-if="rule.action_type === 'conversation'">
 <label
 class="block text-[10px] font-medium text-n-slate-10 mb-0.5"
 >
 Inbox
 </label>
 <select
 :value="rule.action_config?.inbox_id || ''"
 class="reset-base w-full h-8 px-2 text-xs rounded-md border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500/30"
 @change="
 updateExitRule(
 index,
 'action_config.inbox_id',
 $event.target.value
 ? Number($event.target.value)
 : null
 )
 "
 >
 <option value="">Selecione um inbox</option>
 <option
 v-for="inbox in inboxes"
 :key="inbox.id"
 :value="inbox.id"
 >
 {{ inbox.name }}
 </option>
 </select>
 </div>
 </div>

 <!-- Logic info -->
 <p
 v-if="exitRules.length > 1"
 class="text-[10px] text-n-slate-9 text-center !mb-0 pt-1"
 >
 A primeira regra que corresponder sera executada.
 </p>
 </div>
 </div>

 <hr class="border-n-weak" />

 <!-- Conversion identifier -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Identificador de conversao
 </label>
 <input
 :value="conversionIdentifier"
 type="text"
 placeholder="ex: form_landing_page"
 class="reset-base w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="
 $emit(
 'update:conversionIdentifier',
 $event.target.value
 )
 "
 />
 <p
 class="text-[10px] text-n-slate-9 !mb-0 mt-1"
 >
 Usado para rastrear conversoes via scripts.
 </p>
 </div>

 <hr class="border-n-weak" />

 <h4
 class="text-xs font-semibold text-n-slate-10 uppercase tracking-wider"
 >
 Aparencia
 </h4>

 <!-- Primary color -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-1"
 >
 Cor primaria
 </label>
 <div class="flex items-center gap-2">
 <input
 :value="primaryColor"
 type="color"
 class="size-9 rounded-lg border border-n-weak cursor-pointer p-0.5"
 @input="
 $emit('update:primaryColor', $event.target.value)
 "
 />
 <input
 :value="primaryColor"
 type="text"
 class="reset-base flex-1 h-9 px-3 text-xs font-mono rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500/30 focus:border-woot-500"
 @input="
 $emit('update:primaryColor', $event.target.value)
 "
 />
 </div>
 </div>

 <!-- Theme toggle -->
 <div>
 <label
 class="block text-xs font-medium text-n-slate-11 mb-2"
 >
 Tema do formulario
 </label>
 <div class="flex gap-1 p-1 bg-n-slate-3 rounded-lg">
 <button
 class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
 :class="
 formTheme === 'light'
 ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
 : 'text-n-slate-10'
 "
 @click="$emit('update:formTheme', 'light')"
 >
 <span class="i-lucide-sun size-3.5" />
 Claro
 </button>
 <button
 class="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
 :class="
 formTheme === 'dark'
 ? 'bg-white dark:bg-n-solid-3 text-n-slate-12 shadow-sm'
 : 'text-n-slate-10'
 "
 @click="$emit('update:formTheme', 'dark')"
 >
 <span class="i-lucide-moon size-3.5" />
 Escuro
 </button>
 </div>
 <p class="text-[10px] text-n-slate-9 !mb-0 mt-1">
 Escolha o tema ideal para o site onde o formulario sera exibido.
 </p>
 </div>

 <!-- Show branding -->
 <div class="flex items-center justify-between">
 <div>
 <label
 class="text-xs font-medium text-n-slate-11"
 >
 Exibir branding
 </label>
 <p
 class="text-[10px] text-n-slate-9 !mb-0"
 >
 Mostra "Powered by" no rodape
 </p>
 </div>
 <button
 class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
 :class="
 showBranding
 ? 'bg-woot-500'
 : 'bg-n-slate-5'
 "
 @click="$emit('update:showBranding', !showBranding)"
 >
 <span
 class="inline-block size-3.5 transform rounded-full bg-white transition-transform shadow-sm"
 :class="
 showBranding ? 'translate-x-4.5' : 'translate-x-1'
 "
 />
 </button>
 </div>
 </div>
</template>
